import { ROUTE_BASE_NAME } from '~/constants'
import type { SearchQueryParameters } from '~/types/query-parameters'
import type { RvUnifiedSearchQuery } from '~/types/rental-api-aliases'
import type { SearchRentalType } from '~/types/search'
import searchFormMessages from '~~/i18n/locales/common/search/form.json'

export default function useSearch() {
  const searchData = useSearchData()
  const isMenuFilterApplied = useIsMenuFilterApplied()
  const localePath = useLocalePath()
  const route = useRoute()
  const { isRenderedRvRentalPage } = useBaseName()
  const { geolocation, isInCaOrUs } = useGeolocation()
  const { t } = useI18n({ useScope: 'global', messages: searchFormMessages })
  const { formatAsShortDateRange } = useDates()

  function applyMenuFilter() {
    isMenuFilterApplied.value = true
  }

  function resetMenuFilter() {
    isMenuFilterApplied.value = false
  }

  const locationLabel = computed(() => [
    searchData.value.location?.name,
    searchData.value.location?.streetName,
    searchData.value.location?.city,
    searchData.value.location?.regionCode,
    searchData.value.location?.country,
  ].filter(Boolean).join(', '))

  const collapsedCampgroundsLocationLabel = computed(() => {
    if (searchData.value.location?.name) {
      return t('searchForm.location.collapsed.campgroundsNear', { name: searchData.value.location.name })
    }

    return t('searchForm.location.collapsed.campgroundsNearby')
  })

  const collapsedRvsLocationLabel = computed(() => {
    if (searchData.value.bounds) {
      return t('searchForm.location.collapsed.rvsInMapArea')
    }

    if (searchData.value.location?.name) {
      return t('searchForm.location.collapsed.rvsNear', { name: searchData.value.location.name })
    }

    return t('searchForm.location.collapsed.rvsNearby')
  })

  const collapsedDatesLabel = computed(() => {
    const { start, end } = searchData.value.dates ?? {}

    if (start && end) {
      return formatAsShortDateRange(start, end)
    }

    return t('searchForm.dates.anyDates')
  })

  const collapsedGuestsLabel = computed(() => {
    const guests = searchData.value.guests

    if (guests?.adults || guests?.children || guests?.petFriendly) {
      return `${t('searchForm.guests.count', { n: (guests.adults ?? 0) + (guests.children ?? 0) })}`
    }

    return t('searchForm.guests.add')
  })

  const appliedRentalType = computed<SearchRentalType | undefined>(() => getAppliedRentalType(searchData.value))

  const collapsedRentalTypeLabel = computed(() => {
    if (appliedRentalType.value) {
      return t(`searchForm.rentalType.${appliedRentalType.value}`)
    }

    return t('searchForm.rentalType.allRVs')
  })

  async function navigateToCampgroundsSearch({ PlaceId }: { PlaceId?: string }) {
    await navigateTo(localePath({
      name: ROUTE_BASE_NAME.campgrounds_search,
      query: {
        ...route.query,
        PlaceId,
        NeLat: undefined,
        NeLng: undefined,
        SwLat: undefined,
        SwLng: undefined,
        page: undefined,
      },
    }))
  }

  /**
   * Applies a change to the search by writing it to the URL, which the search
   * middleware then reads back into the applied search.
   *
   * Only the parameters the caller mentions are written, so a filter toggle keeps the
   * current location and a map move keeps the current filters. Any page the caller is
   * on is left behind, since the results are no longer the ones that were paged.
   *
   * @param parameters The search parameters to apply.
   */
  async function navigateToRvSearch(parameters: SearchQueryParameters = {}) {
    await navigateTo(localePath({
      name: ROUTE_BASE_NAME.rv_search,
      query: {
        // Because we're using the same query parameters for Estimate, we need to omit the query parameters for the current page.
        ...isRenderedRvRentalPage.value ? {} : { ...route.query },
        ...serializeSearchParameters(parameters),
        Page: parameters.Page && parameters.Page > 1 ? parameters.Page : undefined,
      },
    }))
  }

  /**
   * Resets every filter, leaving the location, dates, guests and sort order alone.
   */
  async function clearRvSearchFilters() {
    await navigateToRvSearch(Object.fromEntries(SEARCH_FILTER_QUERY_PARAMETERS
      .map((queryParameter) => [queryParameter, undefined]),
    ))
  }

  const rvSearchApiRequestParams = computed<RvUnifiedSearchQuery>(() => getSearchApiParameters(searchData.value, {
    geolocation: geolocation.value,
    isInCaOrUs: isInCaOrUs.value,
  }))

  return {
    appliedRentalType,
    searchData,
    isMenuFilterApplied,
    locationLabel,
    collapsedCampgroundsLocationLabel,
    collapsedRvsLocationLabel,
    collapsedDatesLabel,
    collapsedGuestsLabel,
    collapsedRentalTypeLabel,
    rvSearchApiRequestParams,
    applyMenuFilter,
    clearRvSearchFilters,
    navigateToCampgroundsSearch,
    navigateToRvSearch,
    resetMenuFilter,
  }
}
